import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Box, 
  Container, 
  Typography, 
  Grid, 
  Button, 
  Card, 
  CardContent,
  TextField,
  List,
  ListItem,
  ListItemText,
  Paper,
  CircularProgress
} from '@mui/material';
import {
  Mic as MicIcon,
  PlayArrow as PlayArrowIcon,
  AccessTime as AccessTimeIcon,
  Search as SearchIcon
} from '@mui/icons-material';
import { HomePageData, SearchSuggestion, AudioPractice } from '../types/home';
import { getHomePageData, getAudioPractices } from '../services/api';
import { homePageStyles } from '../styles';

const HomePage: React.FC = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const navigate = useNavigate();
  const [homeData, setHomeData] = useState<HomePageData | null>(null);
  const [audioPractices, setAudioPractices] = useState<AudioPractice[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true);
        console.log('Fetching data...');
        
        // Сначала получаем данные главной страницы
        const homeDataResponse = await getHomePageData();
        console.log('Home data received:', homeDataResponse);
        setHomeData(homeDataResponse);

        // Затем получаем аудио практики
        const audioPracticesResponse = await getAudioPractices();
        console.log('Audio practices received:', audioPracticesResponse);
        // Проверяем, является ли ответ объектом с массивом results
        const practices = Array.isArray(audioPracticesResponse) 
          ? audioPracticesResponse 
          : audioPracticesResponse.results || [];
        setAudioPractices(practices);
      } catch (err) {
        console.error('Error in fetchData:', err);
        setError(err instanceof Error ? err.message : 'Произошла ошибка при загрузке данных');
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, []);

  if (isLoading) {
    return (
      <Container maxWidth="lg" sx={homePageStyles.loadingContainer}>
        <CircularProgress sx={homePageStyles.loadingSpinner} />
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={homePageStyles.errorContainer}>
        <Paper sx={homePageStyles.errorPaper}>
          <Typography color="error" gutterBottom>
            {error}
          </Typography>
          <Button 
            variant="contained" 
            onClick={() => window.location.reload()}
            sx={homePageStyles.errorButton}
          >
            Попробовать снова
          </Button>
        </Paper>
      </Container>
    );
  }

  if (!homeData) {
    return (
      <Container maxWidth="lg" sx={homePageStyles.errorContainer}>
        <Paper sx={homePageStyles.errorPaper}>
          <Typography gutterBottom>
            Нет данных для отображения
          </Typography>
          <Button 
            variant="contained" 
            onClick={() => window.location.reload()}
            sx={homePageStyles.errorButton}
          >
            Обновить страницу
          </Button>
        </Paper>
      </Container>
    );
  }

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value);
    setShowSuggestions(true);
  };

  const handleSuggestionClick = (suggestion: SearchSuggestion) => {
    setSearchQuery(suggestion.text);
    setShowSuggestions(false);
  };

  const handleEmotionalStateClick = (state: string) => {
    navigate(`/quick-help/${state}`);
  };

  const formatDuration = (seconds: number): string => {
    const minutes = Math.floor(seconds / 60);
    return `${minutes} мин`;
  };

  return (
    <Container maxWidth="lg" sx={homePageStyles.container}>
      {/* Search Section */}
      <Box sx={homePageStyles.section}>
        <Typography variant="h4" sx={homePageStyles.sectionTitle}>
          Расскажите о вашей ситуации
        </Typography>
        <Box sx={homePageStyles.searchContainer}>
          <TextField
            fullWidth
            variant="outlined"
            placeholder="Поиск..."
            value={searchQuery}
            onChange={handleSearchChange}
            sx={homePageStyles.searchField}
            InputProps={{
              startAdornment: <SearchIcon sx={homePageStyles.searchIcon} />,
            }}
          />
          {showSuggestions && (
            <Paper sx={homePageStyles.suggestionsPaper}>
              {homeData.searchSuggestions.length > 0 ? (
                <List>
                  {homeData.searchSuggestions.map((suggestion) => (
                    <ListItem 
                      key={suggestion.id} 
                      button 
                      onClick={() => handleSuggestionClick(suggestion)}
                      sx={homePageStyles.suggestionItem}
                    >
                      <ListItemText 
                        primary={suggestion.text}
                        sx={homePageStyles.suggestionText}
                      />
                    </ListItem>
                  ))}
                </List>
              ) : (
                <ListItem 
                  button 
                  onClick={() => handleSuggestionClick({ id: 0, text: 'Пообщаться с AI психологом' })}
                  sx={homePageStyles.suggestionItem}
                >
                  <ListItemText 
                    primary="Пообщаться с AI психологом"
                    sx={homePageStyles.suggestionText}
                  />
                </ListItem>
              )}
            </Paper>
          )}
        </Box>
      </Box>

      {/* Emotional States Section */}
      <Box sx={homePageStyles.section}>
        <Typography variant="h4" sx={homePageStyles.sectionTitle}>
          Как вы себя чувствуете?
        </Typography>
        <Grid container spacing={homePageStyles.emotionalStatesGrid.spacing} sx={{ mb: homePageStyles.emotionalStatesGrid.mb }}>
          {homeData.emotionalStates.map((state) => (
            <Grid item xs={6} sm={4} md={2} key={state.id}>
              <Button
                fullWidth
                variant="text"
                sx={homePageStyles.emotionalStateButton}
                onClick={() => handleEmotionalStateClick(state.state)}
              >
                <Typography variant="h3" sx={homePageStyles.emotionalStateIcon}>{state.icon}</Typography>
                <Typography variant="body2" align="center" sx={homePageStyles.emotionalStateText}>
                  {state.label}
                </Typography>
              </Button>
            </Grid>
          ))}
        </Grid>
      </Box>

      {/* Chat Section */}
      <Box sx={homePageStyles.section}>
        <Typography variant="h4" sx={homePageStyles.sectionTitle}>
          Пообщаться с AI психологом
        </Typography>
        <Card sx={homePageStyles.chatCard}>
          <CardContent sx={homePageStyles.chatCardContent}>
            <Typography variant="h5" sx={homePageStyles.chatTitle}>
              Привет! Как ты себя чувствуешь сегодня?
            </Typography>
            <Box sx={homePageStyles.chatButtonContainer}>
              <Button 
                variant="contained" 
                sx={homePageStyles.chatButton}
                startIcon={<MicIcon />}
                onClick={() => navigate('/chat')}
              >
                Начать разговор
              </Button>
              <Box sx={homePageStyles.chatTimeInfo}>
                <AccessTimeIcon sx={homePageStyles.chatTimeIcon} />
                <Typography variant="body1" sx={homePageStyles.chatTimeText}>
                  20 минут бесплатно
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>
      </Box>

      {/* Popular Topics Section */}
      <Box sx={homePageStyles.section}>
        <Typography variant="h4" sx={homePageStyles.sectionTitle}>
          Популярные темы
        </Typography>
        <Box sx={homePageStyles.horizontalScrollContainer}>
          {homeData.popularTopics.map((topic) => (
            <Card 
              key={topic.id}
              sx={{ ...homePageStyles.card, ...homePageStyles.topicCard }}
              onClick={() => navigate(`/topics/${topic.slug}`)}
            >
              <CardContent sx={homePageStyles.cardContent}>
                <Typography variant="h3" sx={homePageStyles.cardIcon}>{topic.icon}</Typography>
                <Typography variant="h6" sx={homePageStyles.cardTitle}>
                  {topic.title}
                </Typography>
                {topic.description && (
                  <Typography variant="body2" sx={homePageStyles.cardDescription}>
                    {topic.description}
                  </Typography>
                )}
              </CardContent>
            </Card>
          ))}
        </Box>
      </Box>

      {/* Audio Practices Section */}
      <Box sx={homePageStyles.section}>
        <Typography variant="h4" sx={homePageStyles.sectionTitle}>
          Аудио-медитации и техники
        </Typography>
        <Box sx={homePageStyles.horizontalScrollContainer}>
          {audioPractices.map((practice) => (
            <Card 
              key={practice.id}
              sx={{ ...homePageStyles.card, ...homePageStyles.audioCard }}
              onClick={() => navigate(`/audio-practices/${practice.id}`)}
            >
              <CardContent sx={homePageStyles.cardContent}>
                <Typography variant="h3" sx={homePageStyles.cardIcon}>{practice.icon}</Typography>
                <Typography variant="h6" sx={homePageStyles.cardTitle}>
                  {practice.title}
                </Typography>
                {practice.description && (
                  <Typography variant="body2" sx={homePageStyles.cardDescription}>
                    {practice.description}
                  </Typography>
                )}
                <Box sx={homePageStyles.audioDurationContainer}>
                  <AccessTimeIcon fontSize="small" sx={homePageStyles.audioDurationIcon} />
                  <Typography variant="body2" sx={homePageStyles.audioDurationText}>
                    {practice.duration || 'Длительность не указана'}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          ))}
        </Box>
        <Box sx={homePageStyles.buttonContainer}>
          <Button 
            variant="outlined" 
            sx={homePageStyles.outlinedButton}
          >
            Слушать все
          </Button>
        </Box>
      </Box>

      {/* Courses Section */}
      <Box sx={homePageStyles.section}>
        <Typography variant="h4" sx={homePageStyles.sectionTitle}>
          Исследуй себя и развивайся
        </Typography>
        <Box sx={homePageStyles.horizontalScrollContainer}>
          {homeData.courses.map((course) => (
            <Card 
              key={course.id}
              sx={{ ...homePageStyles.card, ...homePageStyles.courseCard }}
              onClick={() => navigate(`/courses/${course.id}`)}
            >
              <CardContent sx={homePageStyles.cardContent}>
                <Typography variant="h6" sx={homePageStyles.cardTitle}>
                  {course.title}
                </Typography>
                <Typography variant="body2" sx={homePageStyles.cardDescription}>
                  {course.description}
                </Typography>
                <Box sx={homePageStyles.courseMetaContainer}>
                  <Typography variant="body2" sx={homePageStyles.courseType}>
                    {course.type}
                  </Typography>
                  <Box sx={homePageStyles.courseDurationContainer}>
                    <AccessTimeIcon fontSize="small" sx={homePageStyles.courseDurationIcon} />
                    <Typography variant="body2" sx={homePageStyles.courseDurationText}>
                      {course.duration_formatted}
                    </Typography>
                  </Box>
                  <Typography variant="body2" sx={homePageStyles.courseLessonsText}>
                    {course.lessons_count} уроков
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          ))}
        </Box>
        <Box sx={homePageStyles.buttonContainer}>
          <Button 
            variant="outlined" 
            sx={homePageStyles.outlinedButton}
            onClick={() => navigate('/courses')}
          >
            Все курсы
          </Button>
        </Box>
      </Box>
    </Container>
  );
};

export default HomePage; 